import { useState, useEffect, useCallback, useRef } from 'react';
import { productsApi, assetsApi } from '../api/products.api';
import type { ProductListParams, ProductListResponse, GenerateAssetRequest } from '../types/product.types';
import { useRetry } from './useRetry';

let startupSyncPromise: Promise<void> | null = null;

async function ensureStartupProductSync(): Promise<void> {
  if (!startupSyncPromise) {
    startupSyncPromise = productsApi
      .sync()
      .then(() => undefined)
      .catch((error) => {
        // Don't block the UI if sync fails; listing can still work with existing DB data.
        console.warn('[useProducts] Startup product sync failed:', error);
      });
  }
  await startupSyncPromise;
}

export function useProducts(params?: ProductListParams) {
  const [products, setProducts] = useState<ProductListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { retry } = useRetry();

  const fetchProducts = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await retry(
        () => productsApi.list(params),
        {
          maxRetries: 3,
          retryDelay: 1000,
        }
      );
      console.log('[useProducts] Received products data:', data);
      console.log('[useProducts] Products array length:', data?.data?.length || 0);
      console.log('[useProducts] Setting products state...');
      setProducts(data);
    } catch (err) {
      console.error('[useProducts] Error fetching products:', err);
      if (!silent) {
        setError(err instanceof Error ? err : new Error('Failed to fetch products. Please try again.'));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.page, params?.limit, params?.search, params?.status, params?.has_asset, retry]);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      await ensureStartupProductSync();
      if (isMounted) {
        await fetchProducts();
      }
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, [fetchProducts]);

  // Poll for processing jobs
  useEffect(() => {
    // Check if there are any processing assets
    const hasProcessingAssets = products?.data?.some(
      (product) => product.asset?.status === 'processing' || product.asset?.status === 'pending'
    );

    if (hasProcessingAssets) {
      // Poll every 5 seconds for status updates (silent refresh to avoid flickering)
      pollingIntervalRef.current = setInterval(() => {
        fetchProducts(true); // silent = true to avoid setting loading state
      }, 5000);
    } else {
      // Clear polling if no processing assets
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [products, fetchProducts]);

  const generateAsset = useCallback(async (productId: string, request: GenerateAssetRequest = {}) => {
    try {
      await assetsApi.generate(productId, request);
      // Refresh products after generation
      await fetchProducts();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to generate asset');
    }
  }, [fetchProducts]);

  const cancelGeneration = useCallback(async (productId: string) => {
    try {
      await assetsApi.cancelGeneration(productId);
      // Refresh products immediately with loading state to show update
      await fetchProducts(false);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to cancel generation');
    }
  }, [fetchProducts]);

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
    generateAsset,
    cancelGeneration,
  };
}

